import React, { useState, useEffect } from 'react';
import { useSecretaryStore } from '../store/secretaryStore';
import { X, Search, User, Clock, Calendar, Check, AlertCircle, Phone, ChevronDown, Lock } from 'lucide-react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { crearCitaManual } from '../services/CalendarService';

interface PerfilPaciente {
  perfil_usuario_id: string;
  nombres_completos: string;
  apellidos_completos: string;
  avatar_url?: string;
}

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export function NuevaCitaSlideOver() {
  const { isNuevaCitaOpen, setNuevaCitaOpen, selectedDate, selectedTimeSlot, setSelectedTimeSlot, workspaceProfesionales, activeWorkspaceId, activeWorkspaceType, activeDoctorId, forceRefreshCalendario, configProfesionales, setLastActionText, addNotificacion } = useSecretaryStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [searchResults, setSearchResults] = useState<PerfilPaciente[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPaciente, setSelectedPaciente] = useState<PerfilPaciente | null>(null);

  // Form states
  const [fecha, setFecha] = useState(selectedDate || new Date().toISOString().split('T')[0]);
  const [horaInicio, setHoraInicio] = useState('09:00');
  const [horaFin, setHoraFin] = useState('09:45');
  const [telefono, setTelefono] = useState('');
  const [cedula, setCedula] = useState('');
  const [nombres, setNombres] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [motivo, setMotivo] = useState('');
  const [montoCobrado, setMontoCobrado] = useState('');
  const [moneda, setMoneda] = useState<'USD'|'EUR'>('USD');
  const [nota, setNota] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [tipoSeleccionado, setTipoSeleccionado] = useState<'ocupado' | 'cita_manual'>('cita_manual');
  const [alcance, setAlcance] = useState<'local' | 'global'>('local');

  const handleHoraInicioChange = (newHoraInicio: string) => {
    setHoraInicio(newHoraInicio);
    
    // Obtener duración por defecto del profesional o 45 min por defecto
    const config = configProfesionales?.[selectedProfesional];
    const duracion = config?.duracion_minutos || 45;

    if (newHoraInicio) {
      try {
        const [h, m] = newHoraInicio.split(':').map(Number);
        const dateObj = new Date();
        dateObj.setHours(h, m + duracion, 0, 0);
        
        const endH = dateObj.getHours().toString().padStart(2, '0');
        const endM = dateObj.getMinutes().toString().padStart(2, '0');
        setHoraFin(`${endH}:${endM}`);
      } catch (e) {
        console.warn('Fallback al calcular la hora de fin:', e);
      }
    }
  };
  
  // Doctor Selection
  const [selectedProfesional, setSelectedProfesional] = useState<string>(
    activeDoctorId || (workspaceProfesionales?.length > 0 ? workspaceProfesionales[0].uuid : (activeWorkspaceId || ''))
  );

  // Sync initial date from store if available
  useEffect(() => {
    if (selectedDate) setFecha(selectedDate);
  }, [selectedDate]);

  // Sync initial time slot if available
  useEffect(() => {
    if (selectedTimeSlot && isNuevaCitaOpen) {
      setHoraInicio(selectedTimeSlot.inicio);
      setHoraFin(selectedTimeSlot.fin);
    }
  }, [selectedTimeSlot, isNuevaCitaOpen]);

  // Sync selected profesional when workspace data loads
  useEffect(() => {
    if (!selectedProfesional || selectedProfesional === activeWorkspaceId) {
      if (activeDoctorId) {
        setSelectedProfesional(activeDoctorId);
      } else if (workspaceProfesionales && workspaceProfesionales.length > 0) {
        setSelectedProfesional(workspaceProfesionales[0].uuid);
      }
    }
  }, [workspaceProfesionales, activeDoctorId, activeWorkspaceId, selectedProfesional]);

  // ── Búsqueda de pacientes (Autocompletado Parcial Estricto) ──────────────
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 3) {
      setSearchResults([]);
      return;
    }

    let isMounted = true;
    const fetchPacientes = async () => {
      setIsSearching(true);
      // Busca en perfiles_usuario estrictamente por Cédula (ADR-004)
      const { data, error } = await supabase
        .from('perfiles_usuario')
        .select('perfil_usuario_id, nombres_completos, apellidos_completos, perfil_dni, avatar_url')
        .ilike('perfil_dni', `%${debouncedSearch}%`)
        .limit(5);
        
      if (isMounted) {
        if (!error && data) {
          setSearchResults(data);
        }
        setIsSearching(false);
      }
    };

    fetchPacientes();
    return () => { isMounted = false; };
  }, [debouncedSearch]);

  const handleSelectPaciente = (p: PerfilPaciente) => {
    setSelectedPaciente(p);
    setSearchQuery(`${p.nombres_completos} ${p.apellidos_completos}`);
    setCedula('');
    setTelefono('');
    setFechaNacimiento('');
    setSearchResults([]);
  };

  const handleClearPaciente = () => {
    setSelectedPaciente(null);
    setSearchQuery('');
    setCedula('');
    setTelefono('');
    setFechaNacimiento('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    const [y, mo, d] = fecha.split('-').map(Number);
    const [startH, startM] = horaInicio.split(':').map(Number);
    const [endH, endM] = horaFin.split(':').map(Number);
    
    const fechaInicioIso = new Date(y, mo - 1, d, startH, startM, 0).toISOString();
    const fechaFinIso = new Date(y, mo - 1, d, endH, endM, 0).toISOString();
    
    if (!selectedProfesional) {
      addNotificacion('Por favor selecciona un profesional antes de agendar.', 'warning');
      setIsSaving(false);
      return;
    }

    const { success, message } = await crearCitaManual({
      fechaInicioIso,
      fechaFinIso,
      profesionalUuid: selectedProfesional,
      pacienteUuid: selectedPaciente ? selectedPaciente.perfil_usuario_id : undefined,
      nuevoPacienteNombre: nombres || searchQuery,
      nuevoPacienteTelefono: telefono,
      nuevoPacienteCedula: cedula,
      nuevoPacienteFechaNacimiento: fechaNacimiento || undefined,
      motivo: motivo || undefined,
      montoCobrado: tipoSeleccionado === 'cita_manual' && montoCobrado ? Number.parseFloat(montoCobrado) : undefined,
      moneda: tipoSeleccionado === 'cita_manual' ? moneda : undefined,
      notaAdicional: nota || undefined,
      activeWorkspaceId,
      activeWorkspaceType,
      tipo: tipoSeleccionado,
      alcance: tipoSeleccionado === 'ocupado' ? alcance : 'local'
    });

    setIsSaving(false);
    if (!success) {
      addNotificacion(`Error al agendar la cita: ${message}`, 'warning');
      return;
    }

    setLastActionText(`Agendaste a ${nombres || searchQuery || 'un paciente'} para el ${fecha}`);
    forceRefreshCalendario();
    setNuevaCitaOpen(false);
    setSelectedTimeSlot(null);
  };

  const renderDropdownContent = () => {
    if (isSearching) {
      return (
        <div className="p-4 flex items-center justify-center gap-2 text-sm text-slate-500 font-medium">
          <div className="h-4 w-4 rounded-full border-2 border-slate-300 border-t-blue-500 animate-spin" />
          Buscando paciente...
        </div>
      );
    }
    
    if (searchResults.length > 0) {
      return (
        <div className="flex flex-col py-1">
          {searchResults.map((p) => (
            <button
              key={p.perfil_usuario_id}
              type="button"
              onClick={() => handleSelectPaciente(p)}
              className="w-full text-left px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-3 transition-colors"
            >
              <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center font-bold text-sm shrink-0 overflow-hidden">
                {p.avatar_url ? (
                  <img src={p.avatar_url} alt={p.nombres_completos} className="h-full w-full object-cover" />
                ) : (
                  p.nombres_completos.charAt(0).toUpperCase()
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate block">
                  {p.nombres_completos} {p.apellidos_completos}
                </span>
                <span className="text-xs text-slate-500 truncate block">Paciente Registrado</span>
              </div>
            </button>
          ))}
        </div>
      );
    }
    
    return (
      <div className="p-5 text-sm text-slate-500 flex flex-col items-center text-center gap-2 bg-slate-50/50 dark:bg-slate-800/50">
        <div className="h-10 w-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-1">
          <AlertCircle className="h-5 w-5 text-slate-400" />
        </div>
        <p className="font-medium text-slate-700 dark:text-slate-300">No se encontraron pacientes registrados</p>
        <p className="text-xs text-slate-500 max-w-62.5">Ingresa sus datos abajo para registrarlo automáticamente en el sistema con esta cédula.</p>
      </div>
    );
  };

  if (!isNuevaCitaOpen) return null;

  let btnClasses = 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/20 hover:shadow-blue-500/40';
  let btnText = 'Agendar Cita';
  
  if (isSaving) {
    btnClasses = 'bg-slate-400 cursor-not-allowed shadow-none';
    btnText = 'Guardando...';
  } else if (tipoSeleccionado === 'ocupado') {
    btnClasses = 'bg-amber-500 hover:bg-amber-400 shadow-amber-500/20 hover:shadow-amber-500/40';
    btnText = 'Bloquear Horario';
  }

  return (
    <>
      {/* Overlay Oscurecido */}
      <div 
        className="fixed inset-0 bg-slate-900/40 dark:bg-slate-900/60 backdrop-blur-sm z-100 animate-in fade-in duration-300"
        onClick={() => { setNuevaCitaOpen(false); setSelectedTimeSlot(null); }}
        aria-hidden="true"
      />

      {/* Slide-over Panel Premium */}
      <div className="fixed top-0 right-0 h-full w-full sm:w-110 bg-white dark:bg-slate-900 shadow-2xl z-101 flex flex-col animate-in slide-in-from-right duration-300 border-l border-slate-200 dark:border-slate-800">
        
        {/* Encabezado */}
        <div className="flex flex-col shrink-0">
          <div className="flex items-center justify-between p-5 pb-4" style={{ padding: '1.25rem', paddingBottom: '1rem', boxSizing: 'border-box' }}>
            <h2 className="text-xl font-black text-slate-800 dark:text-white flex items-center gap-3">
              <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center">
                <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              Agendar Nueva Cita
            </h2>
            <button 
              type="button"
              onClick={() => { setNuevaCitaOpen(false); setSelectedTimeSlot(null); }}
              className="p-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full text-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Cerrar ventana"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="h-px w-full bg-linear-to-r from-transparent via-slate-200 dark:via-slate-800 to-transparent" />
        </div>

        {/* Contenido Desplazable */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-8 custom-scrollbar" style={{ padding: '1.5rem', boxSizing: 'border-box' }}>
          
          {/* Selector de Tipo (Agregado para la funcionalidad de Bloqueo) */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 shrink-0">
            <button
              type="button"
              onClick={() => setTipoSeleccionado('cita_manual')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
                tipoSeleccionado === 'cita_manual'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
              }`}
            >
              <User className="h-4 w-4" />
              Cita Manual
            </button>
            <button
              type="button"
              onClick={() => setTipoSeleccionado('ocupado')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${
                tipoSeleccionado === 'ocupado'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
              }`}
            >
              <Lock className="h-4 w-4" />
              Bloquear Horario
            </button>
          </div>
          
          {/* SECCIÓN 1: Paciente */}
          {tipoSeleccionado === 'cita_manual' && (
            <section className="flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-1">
              <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Datos del Paciente
              </h3>
            </div>
            
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Ingresar Cédula de Identidad del paciente..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (selectedPaciente) setSelectedPaciente(null);
                  }}
                  style={{ paddingLeft: '3rem', paddingRight: '3rem', boxSizing: 'border-box' }}
                  className="w-full h-14 pl-12 pr-12 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-slate-400 shadow-sm"
                />
                {selectedPaciente && (
                  <button 
                    type="button"
                    onClick={handleClearPaciente}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-full text-slate-600 dark:text-slate-300 transition-colors"
                    title="Limpiar paciente"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Dropdown Resultados */}
              {!selectedPaciente && searchQuery.length >= 3 && (
                <div className="absolute top-[calc(100%+8px)] left-0 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden z-20">
                  {renderDropdownContent()}
                </div>
              )}
            </div>

            {/* Campos Adicionales si es nuevo (Shadow Profile) */}
            {!selectedPaciente && searchQuery.length > 0 && (
              <div className="animate-in fade-in slide-in-from-top-4 duration-300 flex flex-col gap-5 mt-4 p-6 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/50 rounded-2xl">
                <div className="flex items-center gap-3 mb-1">
                  <span className="flex h-6 w-6 bg-amber-100 dark:bg-amber-900/30 items-center justify-center rounded-full">
                    <User className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                  </span>
                  <span className="text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                    Registrar Nuevo Paciente
                  </span>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Nombres y Apellidos del paciente"
                      value={nombres}
                      onChange={(e) => setNombres(e.target.value)}
                      style={{ paddingLeft: '3rem', boxSizing: 'border-box' }}
                      className="w-full h-14 pl-12 pr-4 bg-white dark:bg-slate-900 border border-amber-200 dark:border-slate-700 rounded-2xl text-base focus:ring-2 focus:ring-amber-500 outline-none transition-shadow"
                    />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="tel"
                      placeholder="Teléfono móvil (Opcional)"
                      value={telefono}
                      onChange={(e) => setTelefono(e.target.value)}
                      style={{ paddingLeft: '3rem', boxSizing: 'border-box' }}
                      className="w-full h-14 pl-12 pr-4 bg-white dark:bg-slate-900 border border-amber-200 dark:border-slate-700 rounded-2xl text-base focus:ring-2 focus:ring-amber-500 outline-none transition-shadow"
                    />
                  </div>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input
                      type="date"
                      value={fechaNacimiento}
                      onChange={(e) => setFechaNacimiento(e.target.value)}
                      style={{ paddingLeft: '3rem', paddingRight: '1rem', boxSizing: 'border-box' }}
                      className="w-full h-14 pl-12 pr-4 bg-white dark:bg-slate-900 border border-amber-200 dark:border-slate-700 rounded-2xl text-base focus:ring-2 focus:ring-amber-500 outline-none transition-shadow text-slate-700 dark:text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
          )}

          {/* SECCIÓN 2: Horario */}
          <section className="flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
                Reserva de Horario
              </h3>
            </div>
            
            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-3xl flex flex-col gap-5" style={{ padding: '1.25rem' }}>
              <div>
                <label htmlFor="nc-prof" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Profesional a cargo</label>
                <div className="relative">
                  <select
                    id="nc-prof"
                    value={selectedProfesional}
                    onChange={(e) => setSelectedProfesional(e.target.value)}
                    style={{ paddingLeft: '1rem', paddingRight: '2.75rem', boxSizing: 'border-box' }}
                    className="w-full h-14 pl-4 pr-11 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow cursor-pointer appearance-none"
                  >
                    {workspaceProfesionales?.map(p => (
                      <option key={p.uuid} value={p.uuid}>{p.nombre}</option>
                    ))}
                    {(!workspaceProfesionales || workspaceProfesionales.length === 0) && (
                      <option value={activeWorkspaceId || ''}>Consultorio General</option>
                    )}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label htmlFor="nc-fecha" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Fecha de la Cita</label>
                <div className="relative">
                  <input
                    id="nc-fecha"
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    style={{ paddingLeft: '1rem', paddingRight: '2.75rem', boxSizing: 'border-box' }}
                    className="w-full h-14 pl-4 pr-11 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nc-inicio" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Hora Inicio</label>
                  <div className="relative">
                    <input
                      id="nc-inicio"
                      type="time"
                      value={horaInicio}
                      onChange={(e) => handleHoraInicioChange(e.target.value)}
                      className="w-full h-14 pl-2 pr-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label htmlFor="nc-fin" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Hora Fin</label>
                  <div className="relative">
                    <input
                      id="nc-fin"
                      type="time"
                      value={horaFin}
                      onChange={(e) => setHoraFin(e.target.value)}
                      className="w-full h-14 pl-2 pr-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                    />
                    <Clock className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {tipoSeleccionado === 'ocupado' && (
            <section className="flex flex-col gap-4">
              <div className="p-5 bg-amber-50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/50 rounded-3xl flex flex-col gap-5">
                <div>
                  <p className="text-xs font-bold text-amber-700 dark:text-amber-500 mb-2 block">Alcance del Bloqueo</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setAlcance('local')}
                      className={`flex-1 flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${
                        alcance === 'local' 
                          ? 'bg-amber-100 border-amber-400 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300 shadow-sm' 
                          : 'bg-white border-amber-200/60 text-slate-500 hover:bg-amber-50/50 dark:bg-slate-900 dark:border-amber-800/30'
                      }`}
                    >
                      <User className="h-5 w-5 mb-1 opacity-80" />
                      <span className="text-sm font-semibold">Agenda Personal</span>
                      <span className="text-[10px] text-center mt-1 leading-tight opacity-70">Aplica solo para este consultorio</span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setAlcance('global')}
                      className={`flex-1 flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${
                        alcance === 'global' 
                          ? 'bg-amber-100 border-amber-400 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-300 shadow-sm' 
                          : 'bg-white border-amber-200/60 text-slate-500 hover:bg-amber-50/50 dark:bg-slate-900 dark:border-amber-800/30'
                      }`}
                    >
                      <Lock className="h-5 w-5 mb-1 opacity-80" />
                      <span className="text-sm font-semibold">Bloqueo Global</span>
                      <span className="text-[10px] text-center mt-1 leading-tight opacity-70">Bloquea también en las Agendas de Equipo</span>
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="nc-nota-bloqueo" className="text-xs font-bold text-amber-700 dark:text-amber-500 mb-1.5 block">Motivo del Bloqueo (Opcional)</label>
                  <textarea 
                    id="nc-nota-bloqueo"
                    placeholder="Ej. El Dr. salió a una emergencia, almuerzo extendido..."
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    style={{ boxSizing: 'border-box' }}
                    className="w-full h-24 p-4 bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-800/50 rounded-2xl text-base focus:ring-2 focus:ring-amber-500 outline-none transition-shadow resize-none"
                  />
                </div>
              </div>
            </section>
          )}

          {/* SECCIÓN 3: Detalles Clínicos y Financieros */}
          {tipoSeleccionado === 'cita_manual' && (
            <section className="flex flex-col gap-4">
            <div className="p-5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-3xl flex flex-col gap-5" style={{ padding: '1.25rem' }}>
              
              <div>
                <label htmlFor="nc-motivo" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Motivo de consulta (Opcional)</label>
                <input 
                  id="nc-motivo"
                  type="text"
                  placeholder="Ej. Control de rutina" 
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  style={{ boxSizing: 'border-box' }}
                  className="w-full h-14 px-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                />
              </div>

              <div>
                <label htmlFor="nc-monto" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Registro de Cobro</label>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <input 
                      id="nc-monto"
                      type="number"
                      step="0.01"
                      placeholder="0.00" 
                      value={montoCobrado}
                      onChange={e => setMontoCobrado(e.target.value)}
                      style={{ boxSizing: 'border-box' }}
                      className="w-full h-14 px-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base focus:ring-2 focus:ring-blue-500 outline-none transition-shadow"
                    />
                  </div>
                  <div className="w-30 relative">
                    <select
                      value={moneda}
                      onChange={e => setMoneda(e.target.value as 'USD' | 'EUR')}
                      style={{ boxSizing: 'border-box' }}
                      className="w-full h-14 px-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base font-medium text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 outline-none transition-shadow appearance-none"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
                <p className="text-[11px] text-emerald-600 mt-2">Nota: Este cobro no pasa por la plataforma digital, es solo para tu registro contable interno.</p>
              </div>

              <div>
                <label htmlFor="nc-nota" className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 block">Nota adicional (Opcional)</label>
                <textarea 
                  id="nc-nota"
                  placeholder='Observaciones de la consulta...'
                  value={nota}
                  onChange={e => setNota(e.target.value)}
                  style={{ boxSizing: 'border-box', minHeight: '100px' }}
                  className="w-full p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-base focus:ring-2 focus:ring-blue-500 outline-none transition-shadow resize-y"
                />
              </div>

            </div>
            
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800/30">
              <AlertCircle className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
              <p className="text-xs font-medium text-blue-700 dark:text-blue-300">
                La cita quedará en estado <span className="font-bold">Confirmada</span>. Podrás cobrarla luego desde el Checkout.
              </p>
            </div>
          </section>
          )}

        </div>

        {/* Footer */}
        <div className="p-6 pt-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shrink-0" style={{ padding: '1.5rem', paddingTop: '1rem', boxSizing: 'border-box' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full h-14 rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg active:scale-[0.98] ${btnClasses}`}
          >
            {isSaving ? (
              <div className="h-5 w-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            ) : (
              <Check className="h-5 w-5" />
            )}
            {btnText}
          </button>
        </div>
      </div>
    </>
  );
}
